import type { Message, ChatContext, APIConfig } from '../../../types/models';
import { formatMessages } from './messageFormatter';
import { APIError } from '../../error/errorHandler';
import { getProviderConfig, getProviderUrl, getProviderEndpoint } from '../../../config/modelsDatabase';
import { usageService } from '../../../services/UsageService';
import { useStore } from '../../../store/useStore';

function cleanJsonResponse(response: string): string {
  // Remove markdown code block markers and any surrounding whitespace
  const cleaned = response.replace(/^```json\s*|\s*```$/g, '').trim();
  
  // If still has backticks, try to extract just the JSON part
  if (cleaned.includes('`')) {
    const match = cleaned.match(/\{[^]*\}/);
    if (match) {
      return match[0];
    }
  }
  
  return cleaned;
}

export async function sendMessage(
  message: string,
  config: APIConfig,
  modelId: string,
  context: ChatContext,
  previousMessages: Message[]
): Promise<string> {
  try {
    if (!config?.apiKey) {
      throw new Error('Deepseek API key is required');
    }

    // Get DeepSeek configuration
    const providerConfig = getProviderConfig('deepseek');
    if (!providerConfig) {
      throw new Error('DeepSeek provider configuration not found');
    }

    const chatEndpoint = getProviderEndpoint('deepseek', 'chat');
    if (!chatEndpoint) {
      throw new Error('DeepSeek chat endpoint not found');
    }

    const isReasoner = modelId === 'deepseek-reasoner';
    const formattedMessages = formatMessages(message, context, previousMessages, modelId);

    // Prepare request body
    // Map the model ID to DeepSeek's naming convention if needed
    let actualModelId = modelId;
    if (modelId === 'deepseek-chat') {
      actualModelId = 'deepseek-chat';
    } else if (modelId === 'deepseek-reasoner') {
      actualModelId = 'deepseek-reasoner';
    }
    
    const requestBody: any = {
      model: actualModelId,
      messages: formattedMessages,
      stream: false
    };

    // Add max_tokens for reasoner model (up to 8K)
    if (isReasoner) {
      requestBody.max_tokens = 8192;
    }

    // Add JSON output format if requested
    if (context.jsonOutput) {
      requestBody.response_format = { type: 'json_object' };
      // Add 'json' to system prompt if not present
      if (!context.systemPrompt.toLowerCase().includes('json')) {
        requestBody.messages.unshift({
          role: 'system',
          content: 'Please provide your response in JSON format.'
        });
      }
    }

    // Log request details (truncate long messages)
    console.log('Sending request to DeepSeek API:', {
      url: getProviderUrl('deepseek', chatEndpoint),
      model: modelId,
      isReasoner,
      hasJsonOutput: !!context.jsonOutput,
      messageCount: requestBody.messages.length
    });

    // Make request with timeout - increased for DeepSeek which can be slow
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
      console.error('DeepSeek API request timed out after 60 seconds');
    }, 60000); // 60-second timeout

    try {
      const response = await fetch(getProviderUrl('deepseek', chatEndpoint), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.apiKey}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...providerConfig.headers // Include any provider-specific headers
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      clearTimeout(timeout);

      // Read response body once
      const responseText = await response.text();
      
      // Check for empty response
      if (!responseText || responseText.trim().length === 0) {
        console.error('Empty response from Deepseek API');
        throw new APIError('Empty response from Deepseek API', 'Deepseek');
      }

      console.log('Raw response:', responseText);

      // Handle non-200 responses
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorDetails = {};
        
        try {
          const errorJson = JSON.parse(responseText);
          errorDetails = errorJson;
          errorMessage = errorJson.error?.message || errorJson.message || errorMessage;
        } catch {
          errorMessage = responseText || errorMessage;
        }

        console.error('DeepSeek API error response:', {
          status: response.status,
          statusText: response.statusText,
          error: errorDetails,
          message: errorMessage
        });

        throw new APIError(errorMessage, 'Deepseek');
      }

      // Parse response
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (jsonError) {
        console.error('Failed to parse Deepseek API response:', {
          status: response.status,
          error: jsonError,
          responseText
        });
        throw new APIError(
          `Invalid JSON response from Deepseek API: ${jsonError instanceof Error ? jsonError.message : 'Unknown parsing error'}`,
          'Deepseek'
        );
      }

      // Validate response structure with detailed checks
      if (!data) {
        throw new APIError('Empty response from Deepseek API', 'Deepseek');
      }
      if (!Array.isArray(data.choices)) {
        throw new APIError('Invalid choices field in response', 'Deepseek');
      }
      if (data.choices.length === 0) {
        throw new APIError('Empty choices array in response', 'Deepseek');
      }
      if (!data.choices[0].message) {
        throw new APIError('No message in response choice', 'Deepseek');
      }

      // Log successful response structure
      console.log('Received valid response structure:', {
        hasChoices: true,
        messagePresent: true,
        contentLength: data.choices[0].message.content?.length || 0,
        hasReasoningContent: isReasoner ? !!data.choices[0].message.reasoning_content : 'N/A'
      });

      // Process response content
      let finalContent = '';
      try {
        if (isReasoner) {
          if (!data.choices[0].message.reasoning_content) {
            throw new APIError('No reasoning content in response', 'Deepseek');
          }
          const reasoning = data.choices[0].message.reasoning_content;
          const answer = data.choices[0].message.content;
          
          finalContent = `<think>${reasoning.trim()}</think>\n\n${answer.trim()}`;
        } else {
          if (!data.choices[0].message.content) {
            throw new APIError('No content in response', 'Deepseek');
          }
          finalContent = data.choices[0].message.content;
        }

        // Validate content length
        if (finalContent.trim().length === 0) {
          throw new APIError('Empty content in response', 'Deepseek');
        }

        // Handle JSON output
        if (context.jsonOutput) {
          try {
            const jsonContent = JSON.parse(cleanJsonResponse(finalContent));
            finalContent = JSON.stringify(jsonContent, null, 2);
          } catch (jsonError) {
            console.error('Invalid JSON response:', jsonError);
            throw new APIError('Response is not valid JSON', 'Deepseek');
          }
        }

        // Record usage if available
        if (data.usage) {
          const { user, topUpValue } = useStore.getState();
          if (user) {
            await usageService.recordUsage(
              user.uid,
              modelId,
              {
                prompt_tokens: data.usage.prompt_tokens,
                completion_tokens: data.usage.completion_tokens,
                total_tokens: data.usage.total_tokens
              },
              topUpValue
            );
          }
        }

        return finalContent;
      } catch (error: unknown) {
        console.error('Error processing Deepseek response:', error);
        throw new APIError(
          `Failed to process Deepseek response: ${error instanceof Error ? error.message : 'Unknown error'}`,
          'Deepseek'
        );
      }
    } catch (fetchError: unknown) {
      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        throw new APIError('Request to DeepSeek API timed out', 'Deepseek');
      }
      throw fetchError;
    } finally {
      clearTimeout(timeout);
    }
  } catch (error) {
    console.error('Deepseek API Error:', {
      error: error instanceof Error ? error.message : 'Unknown error',
      baseUrl: getProviderUrl('deepseek')
    });

    if (error instanceof Error) {
      if (error.message.includes('API key')) {
        throw new APIError('Deepseek API key is required', 'Deepseek');
      }
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new APIError('Could not connect to Deepseek API. Please check your internet connection.', 'Deepseek');
      }
    }
    
    throw error instanceof APIError ? error : new APIError(
      error instanceof Error ? error.message : 'Failed to communicate with Deepseek',
      'Deepseek'
    );
  }
}